import { Fragment, useMemo } from "react";
import { motion } from "framer-motion";

/**
 * SplitText — a cinema-style reveal for headings.
 *
 * Splits `children` (or the `text` prop) into words, then into characters,
 * and animates each character from a blurred / translated state.
 *
 * Supported markup inside `text`:
 *   - "\n"         → line break (each line animates in its own stagger)
 *   - "*word*"     → editorial italic accent (uses Instrument Serif italic)
 *
 * Motion values tuned to feel institutional, not showy:
 *   - stagger ~14ms per character
 *   - duration 0.9s with soft cubic ease
 *   - opacity + y(20px) + blur(6px)
 *
 * Accessibility: the full plain-text string is exposed via aria-label,
 * and the animated glyphs are aria-hidden.
 */
export default function SplitText({
  text = "",
  children,
  as: Tag = "span",
  className = "",
  delay = 0,
  duration = 0.9,
  stagger = 0.014,
  lineStagger = 0.06,
  once = true,
  "data-testid": testId,
}) {
  const raw = typeof children === "string" ? children : text;
  const lines = useMemo(() => String(raw || "").split(/\n/), [raw]);

  // Flat character index (for a smooth global stagger across lines).
  let charCounter = 0;

  return (
    <Tag
      className={className}
      aria-label={raw}
      data-testid={testId}
    >
      {lines.map((line, li) => {
        // Segments alternating plain / italic (*...*)
        const segments = line.split(/(\*[^*]+\*)/g).filter(Boolean);

        return (
          <span
            key={li}
            aria-hidden="true"
            className="block overflow-hidden"
            style={{ paddingBottom: "0.08em" }}
          >
            {segments.map((seg, si) => {
              const italic = seg.startsWith("*") && seg.endsWith("*") && seg.length > 2;
              const clean = italic ? seg.slice(1, -1) : seg;
              const words = clean.split(/(\s+)/); // keep spaces as their own tokens
              return (
                <Fragment key={si}>
                  {words.map((word, wi) => {
                    if (/^\s+$/.test(word)) {
                      return (
                        <span key={wi} aria-hidden="true">
                          &nbsp;
                        </span>
                      );
                    }
                    return (
                      <span
                        key={wi}
                        className="inline-block whitespace-nowrap align-baseline"
                      >
                        {Array.from(word).map((ch, ci) => {
                          const idx = charCounter++;
                          return (
                            <motion.span
                              key={ci}
                              initial={{ opacity: 0, y: "0.6em", filter: "blur(6px)" }}
                              whileInView={{
                                opacity: 1,
                                y: 0,
                                filter: "blur(0px)",
                              }}
                              viewport={{ once, margin: "-15%" }}
                              transition={{
                                duration,
                                delay: delay + idx * stagger + li * lineStagger,
                                ease: [0.2, 0.7, 0.2, 1],
                              }}
                              className={
                                italic
                                  ? "inline-block font-serif italic text-[#C48F56]"
                                  : "inline-block"
                              }
                              style={{ willChange: "transform, opacity, filter" }}
                            >
                              {ch}
                            </motion.span>
                          );
                        })}
                      </span>
                    );
                  })}
                </Fragment>
              );
            })}
          </span>
        );
      })}
    </Tag>
  );
}
